import { Injectable } from '@angular/core';
import { Command, Random, StateService } from 'flux-core';
import cloneDeep from 'lodash/cloneDeep';
import { ShapeType } from 'flux-definition/src';
import { DiagramChangeService } from '../../../base/diagram/diagram-change.svc';
import { AbstractDiagramChangeCommand } from './abstract-diagram-change-command.cmd';
import { Restriction } from '../restriction/restriction.svc';
import { SvgToCanvas } from 'flux-diagram-composer';

/**
 * AddFreePathShape command to add and update freepath shape
 *
 * @author  hashancgx
 * @since   2026-06-15
 *
 */
@Injectable()
@Command()
export class AddFreePathShape extends AbstractDiagramChangeCommand {

    /**
     * Command input data format
     */
    public data: {
        svg: string;
        bounds: {
            x: number,
            y: number,
            width: number,
            height: number,
        };
        shapeId: string;
    };

    /**
     * SvgToCanvas instance
     */
    protected canvasInstance: SvgToCanvas;

    /**
     * Freehand def id
     */
    protected freehandDefId = 'creately.basic.freepath';

    /**
     * Freehand default bounds from def
     */
    protected defaultBounds = 200;

    /**
     * Current freehand version
     */
    protected freehandVersion = 1;

    protected styleMap = {
        lineStyle: 'stroke-dasharray',
        lineThickness: 'stroke-width',
        lineColor: 'stroke',
    };

    /**
     * Inject the state service to get the list of selected shape ids.
     */
    constructor( protected state: StateService<any, any>,
                 protected ds: DiagramChangeService,
                 protected restriction: Restriction,
    ) {
        super( ds ) /* istanbul ignore next*/;
        this.canvasInstance = new SvgToCanvas();
    }

    /**
     * Invoke the alterFunction on the changeModel.
     */
    public prepareData() {
        if ( !this.data || !this.hasPropertiesSet([ 'svg', 'bounds' ]) ||
            !this.hasPropertiesSet([ 'width', 'height', 'x', 'y' ], this.data.bounds )) {
                return;
        }
        this.resultData = { shapeId: null };

        const freehandStateStyles = { 'stroke-width': 1, 'stroke': '#000000', 'stroke-dasharray': '' };
        const styles = {
            lineThickness: freehandStateStyles[ this.styleMap.lineThickness ],
            lineStyle: freehandStateStyles[ this.styleMap.lineStyle ].split( ', ' ).map( (val: any) => +val ),
            lineColor: freehandStateStyles[ this.styleMap.lineColor ]
        };

        if ( !!this.data.shapeId ) {
            const shape: any = this.changeModel.shapes[ this.data.shapeId ];
            // Update bounds
            shape.x = this.data.bounds.x; // - styles.lineThickness / 2;
            shape.y = this.data.bounds.y; // - styles.lineThickness / 2;
            shape.defaultBounds.width = this.data.bounds.width; // + styles.lineThickness;
            shape.defaultBounds.height = this.data.bounds.height; // + styles.lineThickness;
            shape.scaleX = 1;
            shape.scaleY = 1;
            // Update instructions
            const k = this.canvasInstance.convert( this.data.svg );
            shape.instructions = k.instructions;
            shape.styleDefinitions= k.styles;
            shape.zIndex = this.changeModel.maxZIndex + 1;
            shape.isSticker = true;
        } else {
            const shapeId = this.getShapeId();
            this.resultData.shapeId = shapeId;
            const k = this.canvasInstance.convert( this.data.svg )

            const shape = {
                id: shapeId,
                defId: this.freehandDefId,
                version: this.freehandVersion,
                scaleX: this.data.bounds.width / this.defaultBounds,
                scaleY: this.data.bounds.height / this.defaultBounds,
                type: ShapeType.Freehand,
                x: this.data.bounds.x,
                y: this.data.bounds.y,
                style: styles,
                styleDefinitions: k.styles,
                instructions: k.instructions,
                zIndex: this.changeModel.maxZIndex + 1,
                isSticker: true,
                defaultBounds: {}
            };

            const position = { x: shape.x || 0, y: shape.y || 0 };
            const restricted = this.restriction.point( position, [ 'GridService' ]);
            shape.x = restricted.x;
            shape.y = restricted.y;

            this.changeModel.shapes[shapeId] = cloneDeep( shape );
        }
    }
    /**
     * Returns a newly generate id for the shape to be added
     */
    protected getShapeId() {
        return Random.shapeId();
    }

    /**
     * Checks whether the modifier sets all given properties, returns false if any of them are missing.
     */
     private hasPropertiesSet( paths: string[], prop?: any ): boolean {
        for ( const path of paths ) {
            const value = prop ? prop[path] : this.data[path];
            if ( value === undefined ) {
                return false;
            }
        }
        return true;
    }
}

Object.defineProperty( AddFreePathShape, 'name', {
    value: 'AddFreePathShape',
});
